import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { brand, useBrandFeatures, useSiteSettings } from '@/brands';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { SiteGate } from '@/components/common/SiteGate';
import { MenuGate } from '@/components/common/MenuGate';
import { FeatureGate } from '@/components/common/FeatureGate';

import { NotFoundPage } from '@/pages/NotFoundPage';
import GameLayout from './layouts/GameLayout';
import MainLayout from './layouts/MainLayout';
import {
  AccountStatementPage,
  ActivityLogsPage,
  AffiliatePage,
  AuthVerificationPage,
  CasinoResultsPage,
  ChangePasswordPage,
  BetHistoryPage,
  HomePage,
  CasinoBetHistory,
  DepositWithdrawPage,
  LoginPage,
  RegisterPage,
  MarketsPage,
  SportsPage,
  TermsConditionsPage,
  CasinoPage,
  QTECHLobbyPage,
  CasinoPlaygroundPage,
  GoalGamePage,
  QTECHCasinoPage,
  FantasyListPage,
  LiveCasinoPage,
  SlotsPage,
  NexxCasinoPage,
  BlankPage,
  DepositPage,
  FixedDepositPage,
  ContactPage,
  WalletHistoryPage,
} from '@/routes';
import { ScrollToTop } from '@/utils/ScrollToTop';
import CasinoLayout from './layouts/CasinoLayout';

export function App() {
  // Site Settings the router itself has to honour: a route that is switched off
  // is not mounted at all, so a hidden button cannot be reached by typing the
  // URL either. The server refuses the matching API calls independently.
  const { signup, showLogin, deposit } = useSiteSettings();
  // Brand Features the router honours alongside them. Site Settings are the
  // brand owner's day-to-day switches; Features are the super admin's grant,
  // and a product they revoked must not be reachable by URL either. Both have
  // to pass — `FeatureGate` below closes the param routes the same way.
  const { registration, deposit: depositOn, withdrawal, fd } = useBrandFeatures();

  return (
    <SiteGate>
      <ScrollToTop />
      <FeatureGate>
        <Routes>
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            {showLogin && <Route path="/login" element={<LoginPage />} />}
            {signup && registration && <Route path="/register" element={<RegisterPage />} />}
          </Route>
          {/* Menu Visibility: a page whose every menu link the super admin hid is
              unreachable by URL too, not just missing a button. */}
          <Route element={<MenuGate />}>
            <Route element={<MainLayout />}>
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/inplay" element={<HomePage />} />
              <Route path="/all-sports/:sportName" element={<SportsPage />} />
              <Route path="/user/account-statement" element={<AccountStatementPage />} />
              <Route path="/user/bet-history" element={<BetHistoryPage />} />
              <Route path="/user/activity-logs" element={<ActivityLogsPage />} />

              <Route path="/user/affiliate" element={<AffiliatePage />} />
              <Route path="/user/casino-results" element={<CasinoResultsPage />} />
              <Route path="/user/casino-bet-history" element={<CasinoBetHistory />} />
              <Route path="/user/secure-auth-verification" element={<AuthVerificationPage />} />
              <Route path="/user/change-password" element={<ChangePasswordPage />} />
              <Route path="/user/wallet-history" element={<WalletHistoryPage />} />
            </Route>
            <Route element={<CasinoLayout />}>
              <Route path="/casino-playground" element={<CasinoPlaygroundPage />} />
              <Route path="/casino/goal-game/:gameId" element={<GoalGamePage />} />
              {/* /casino/* is served by whichever catalog the brand runs on: the
                  aggregator-backed lobby, or the original provider-backed one. */}
              {brand.features.aggregator ? (
                <>
                  <Route path="/casino/" element={<NexxCasinoPage />} />
                  <Route path="/casino/:provider" element={<NexxCasinoPage />} />
                  <Route path="/casino/:provider/:category" element={<NexxCasinoPage />} />
                </>
              ) : (
                <>
                  <Route path="/casino/" element={<CasinoPage />} />
                  <Route path="/casino/:gameType/:subTab" element={<CasinoPage />} />
                  <Route path="/casino/:gameType" element={<CasinoPage />} />
                </>
              )}
              <Route path="/casino-qtech/" element={<QTECHLobbyPage />} />
              <Route path="/qtech/casino/:providerName/:categoryName" element={<QTECHCasinoPage />} />
              {/* Feature-gated routes — only mounted when the active brand enables them. */}
              {brand.features.fantasy && (
                <Route path="/fantasy-list" element={<FantasyListPage />} />
              )}
              {brand.features.liveCasino && (
                <>
                  <Route path="/live-casino" element={<LiveCasinoPage />} />
                  <Route path="/live-casino/:provider" element={<LiveCasinoPage />} />
                  <Route path="/live-casino/:provider/:category" element={<LiveCasinoPage />} />
                </>
              )}
              {brand.features.slots && (
                <>
                  <Route path="/slots" element={<SlotsPage />} />
                  <Route path="/slots/:provider" element={<SlotsPage />} />
                  <Route path="/slots/:provider/:category" element={<SlotsPage />} />
                </>
              )}
            </Route>
          </Route>
          <Route element={<GameLayout />}>
            <Route path="/game-details/:sportName/:eventId" element={<MarketsPage />} />
          </Route>

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              {deposit && withdrawal && (
                <Route path="/user/deposit-withdraw" element={<DepositWithdrawPage />} />
              )}
              {deposit && depositOn && <Route path="/user/deposit" element={<DepositPage />} />}
              {deposit && fd && <Route path="/user/fd" element={<FixedDepositPage />} />}
            </Route>
          </Route>

          {/* 404 */}
          <Route path="/blank" element={<BlankPage />} />
          <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </FeatureGate>
    </SiteGate>
  );
}
  